import {
  FetchMonitorLocationsQuery,
  FetchMonitorLocationsQueryVariables,
  type MonitorLocationFilter,
} from 'app/__generated__/global';
import { FETCH_MONITOR_LOCATIONS } from 'app/business-logic/services/monitor-service/graphql/facility.fetchMonitorLocations';
import { LogManager } from 'core/logging/LogManager';
import { ApolloProvider } from 'core/net/ApolloProvider';
import Guid from 'core/types/Guid';

const logger = LogManager.getLogger('fetchMonitorLocations');

type MonitorLocationHistoryItem = {
  id: Guid;
  latitude: number;
  longitude: number;
  activeFromTime: string;
  activeToTime?: string | null;
  elevation?: number | null;
  project?: string | null;
};

export const fetchMonitorLocations = async ({
  processIds,
  monitorLocationFilter,
  includeHidden = false,
}: {
  processIds: Guid[];
  monitorLocationFilter?: MonitorLocationFilter;
  includeHidden?: boolean;
}) => {
  const { data, error } = await ApolloProvider.global().query<
    FetchMonitorLocationsQuery,
    FetchMonitorLocationsQueryVariables
  >({
    query: FETCH_MONITOR_LOCATIONS,
    variables: { processFilter: { id_in: processIds, include_hidden: includeHidden }, monitorLocationFilter },
    errorPolicy: 'ignore',
  });

  if (error || !data?.facility?.processes) {
    throw new Error(`Failed to get monitor locations`);
  }

  const { withoutLocationHistory, withLocationHistory } = separateByLocationHistory(data.facility.processes);

  if (withoutLocationHistory.length) {
    logger.warn(`The following monitors have no location history and will be filtered out:`, withoutLocationHistory);
  }

  return withLocationHistory;
};

function separateByLocationHistory(processes: NonNullable<FetchMonitorLocationsQuery['facility']>['processes']) {
  const withoutLocationHistory: Array<{ id: Guid; name: string }> = [];
  const withLocationHistory: Array<{
    id: Guid;
    locationHistory: Array<MonitorLocationHistoryItem>;
  }> = [];

  processes.forEach(monitor => {
    const { id } = monitor;
    if (!('locationHistory' in monitor)) {
      withoutLocationHistory.push({ id: monitor.id, name: monitor.name });
      return;
    }
    const validatedLocationHistory =
      monitor.locationHistory?.flatMap(
        ({ id, activeFromTime, activeToTime, latitude, longitude, elevation, project }) => {
          if (!activeFromTime) return [];
          return {
            id,
            activeFromTime,
            activeToTime,
            latitude,
            longitude,
            elevation,
            project,
          };
        }
      ) ?? [];
    if (!validatedLocationHistory.length) {
      withoutLocationHistory.push({ id: monitor.id, name: monitor.name });
      return;
    }
    withLocationHistory.push({
      id,
      locationHistory: validatedLocationHistory,
    });
  });

  return {
    withoutLocationHistory: withoutLocationHistory.sort((a, b) => a.name.localeCompare(b.name)),
    withLocationHistory,
  };
}
